import { useState, useEffect } from 'react';
import { api, Client, DemandOverTimePoint, Strategy, MarketMood } from '../lib/api';

const STRATEGY_COLORS: Record<Strategy, string> = {
  low_volatility: '#6366f1',
  sp500_tilts: '#8b5cf6',
  sector_rotation: '#ec4899',
  dynamic_hedge: '#f43f5e',
  risk_overlay: '#f97316',
  core_equity: '#22c55e',
  novel: '#14b8a6',
};

const STRATEGY_LABELS: Record<Strategy, string> = {
  low_volatility: 'Low Vol',
  sp500_tilts: 'S&P Tilts',
  sector_rotation: 'Sector Rotation',
  dynamic_hedge: 'Dynamic Hedge',
  risk_overlay: 'Risk Overlay',
  core_equity: 'Core Equity',
  novel: 'Novel',
};

const MARKET_MOOD_OPTIONS: { value: MarketMood | ''; label: string }[] = [
  { value: '', label: 'All Moods' },
  { value: 'panic', label: 'Panic' },
  { value: 'bear', label: 'Bear' },
  { value: 'bull', label: 'Bull' },
  { value: 'euphoric', label: 'Euphoric' },
  { value: 'unknown', label: 'Unknown' },
];

export function DemandOverTime() {
  const [clients, setClients] = useState<Client[]>([]);
  const [data, setData] = useState<DemandOverTimePoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters
  const [groupBy, setGroupBy] = useState<'month' | 'quarter'>('month');
  const [clientId, setClientId] = useState<string>('');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [marketMood, setMarketMood] = useState<MarketMood | ''>('');
  const [showEndClientOnly, setShowEndClientOnly] = useState(false);

  // Computed data
  const [periods, setPeriods] = useState<string[]>([]);
  const [strategies, setStrategies] = useState<Strategy[]>([]);
  const [maxCount, setMaxCount] = useState(1);

  useEffect(() => {
    loadClients();
  }, []);

  useEffect(() => {
    loadData();
  }, [groupBy, clientId, dateFrom, dateTo, marketMood]);

  const loadClients = async () => {
    try {
      const clientList = await api.listClients();
      setClients(clientList);
    } catch {
      // Silent fail
    }
  };

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await api.getDemandOverTime({
        group_by: groupBy,
        client_id: clientId || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        market_mood: marketMood || undefined,
      });
      setData(result);

      // Compute unique periods and strategies
      const uniquePeriods = [...new Set(result.map((d) => d.period))].sort();
      const uniqueStrategies = [...new Set(result.map((d) => d.strategy))] as Strategy[];

      // Find max count for scaling
      const max = Math.max(
        ...result.map((d) => (showEndClientOnly ? d.end_client_count : d.mention_count)),
        1
      );

      setPeriods(uniquePeriods);
      setStrategies(uniqueStrategies);
      setMaxCount(max);
    } catch (err) {
      setError('Failed to load demand data');
    } finally {
      setLoading(false);
    }
  };

  const getDataForCell = (period: string, strategy: Strategy): DemandOverTimePoint | undefined => {
    return data.find((d) => d.period === period && d.strategy === strategy);
  };

  const getBarWidth = (count: number): string => {
    return `${Math.max((count / maxCount) * 100, 0)}%`;
  };

  // Calculate totals per period
  const getPeriodTotal = (period: string): number => {
    return data
      .filter((d) => d.period === period)
      .reduce((sum, d) => sum + (showEndClientOnly ? d.end_client_count : d.mention_count), 0);
  };

  // Calculate totals per strategy
  const getStrategyTotal = (strategy: Strategy): number => {
    return data
      .filter((d) => d.strategy === strategy)
      .reduce((sum, d) => sum + (showEndClientOnly ? d.end_client_count : d.mention_count), 0);
  };

  const grandTotal = data.reduce(
    (sum, d) => sum + (showEndClientOnly ? d.end_client_count : d.mention_count),
    0
  );

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Demand Over Time</h1>
        <p className="page-subtitle">Track strategy demand trends across client conversations</p>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Filters */}
      <div className="filters-bar">
        <div className="filter-group">
          <label className="form-label">Group By</label>
          <select
            className="form-select"
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value as 'month' | 'quarter')}
          >
            <option value="month">Month</option>
            <option value="quarter">Quarter</option>
          </select>
        </div>

        <div className="filter-group">
          <label className="form-label">Client</label>
          <select
            className="form-select"
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
          >
            <option value="">All Clients</option>
            {clients.map((c) => (
              <option key={c.client_id} value={c.client_id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label className="form-label">Date From</label>
          <input
            type="date"
            className="form-input"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label className="form-label">Date To</label>
          <input
            type="date"
            className="form-input"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label className="form-label">Market Mood</label>
          <select
            className="form-select"
            value={marketMood}
            onChange={(e) => setMarketMood(e.target.value as MarketMood | '')}
          >
            {MARKET_MOOD_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label className="form-label">Demand Type</label>
          <div className="flex items-center gap-1">
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={showEndClientOnly}
                onChange={(e) => setShowEndClientOnly(e.target.checked)}
              />
              <span className="text-small">End-Client Demand Only</span>
            </label>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="demand-legend">
        {strategies.map((strategy) => (
          <div key={strategy} className="legend-item">
            <span
              className="legend-color"
              style={{ backgroundColor: STRATEGY_COLORS[strategy] }}
            />
            <span className="legend-label">{STRATEGY_LABELS[strategy]}</span>
            <span className="legend-count">({getStrategyTotal(strategy)})</span>
          </div>
        ))}
      </div>

      {/* Chart Area */}
      {loading ? (
        <div className="loading">Loading...</div>
      ) : periods.length === 0 ? (
        <div className="empty-state">
          <p>No strategy demand data available for the selected filters.</p>
        </div>
      ) : (
        <>
          {/* Summary Stats */}
          <div className="stats-grid" style={{ marginBottom: '1.5rem' }}>
            <div className="stat-card">
              <div className="stat-value">{grandTotal}</div>
              <div className="stat-label">Total Mentions</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{periods.length}</div>
              <div className="stat-label">{groupBy === 'month' ? 'Months' : 'Quarters'}</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{strategies.length}</div>
              <div className="stat-label">Strategies</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">
                {periods.length > 0 ? (grandTotal / periods.length).toFixed(1) : '0'}
              </div>
              <div className="stat-label">Avg per Period</div>
            </div>
          </div>

          {/* Stacked Bar Chart (CSS-based) */}
          <div className="demand-chart">
            {periods.map((period) => (
              <div key={period} className="demand-chart-row">
                <div className="demand-chart-label">{period}</div>
                <div className="demand-chart-bars">
                  {strategies.map((strategy) => {
                    const point = getDataForCell(period, strategy);
                    const count = point
                      ? showEndClientOnly
                        ? point.end_client_count
                        : point.mention_count
                      : 0;
                    if (count === 0) return null;
                    return (
                      <div
                        key={strategy}
                        className="demand-bar"
                        style={{
                          width: getBarWidth(count),
                          backgroundColor: STRATEGY_COLORS[strategy],
                        }}
                        title={`${STRATEGY_LABELS[strategy]}: ${count}`}
                      >
                        {count >= 2 && <span className="demand-bar-label">{count}</span>}
                      </div>
                    );
                  })}
                </div>
                <div className="demand-chart-total">{getPeriodTotal(period)}</div>
              </div>
            ))}
          </div>

          {/* Data Table */}
          <div className="mt-2">
            <h3 style={{ marginBottom: '0.75rem' }}>Detailed Data</h3>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Period</th>
                    {strategies.map((s) => (
                      <th key={s}>{STRATEGY_LABELS[s]}</th>
                    ))}
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {periods.map((period) => (
                    <tr key={period}>
                      <td>{period}</td>
                      {strategies.map((strategy) => {
                        const point = getDataForCell(period, strategy);
                        const count = point
                          ? showEndClientOnly
                            ? point.end_client_count
                            : point.mention_count
                          : 0;
                        return (
                          <td key={strategy} style={{ textAlign: 'center' }}>
                            {count > 0 ? count : '-'}
                          </td>
                        );
                      })}
                      <td style={{ textAlign: 'center', fontWeight: 600 }}>
                        {getPeriodTotal(period)}
                      </td>
                    </tr>
                  ))}
                  <tr style={{ fontWeight: 600, backgroundColor: 'var(--bg-tertiary)' }}>
                    <td>Total</td>
                    {strategies.map((strategy) => (
                      <td key={strategy} style={{ textAlign: 'center' }}>
                        {getStrategyTotal(strategy)}
                      </td>
                    ))}
                    <td style={{ textAlign: 'center' }}>{grandTotal}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
